import {ApolloClient, gql} from '@apollo/client';
import {ReportFilter} from '../Models/ReportFilter';
import {errorResponse, NetworkResult, successResponse} from './NetworkResult';
import {Report} from '../Models/Report';
import {ReportTemplate} from '../Ui/Report/Show/Trend/Models/ReportTemplate';
import {ReportType} from '../Models/ReportType';

const GET_REPORT_QUERY = gql`
    query GetReport($id: ID!) {
        report(id: $id) {
            id
            type
            name
            description
            is_locked
            user_ids
            crew_ids
            snippet_collection_ids
            from_iso8601
            till_iso8601
            latest_snippet_at_iso8601
            crews {
                id
                name
            }
            users {
                id
                display_name
            }
            snippetCollections {
                id
                name
            }
            template {
                trends {
                    title
                    rows {
                        type
                        fields
                        x
                        y
                    }
                }
            }
        }
    }
`;
const GET_REPORTS_QUERY = gql`
    query GetReports {
        reports {
            id
            type
            name
            description
            is_locked
            user_ids
            crew_ids
            snippet_collection_ids
            from_iso8601
            till_iso8601
            latest_snippet_at_iso8601
            crews {
                id
                name
            }
            users {
                id
                display_name
            }
            snippetCollections {
                id
                name
            }
            template {
                trends {
                    title
                    rows {
                        type
                        fields
                        x
                        y
                    }
                }
            }
        }
    }
`;

export class ReportService {
    constructor(private client: ApolloClient<any>) {}

    public async get(id: string): Promise<NetworkResult<Report>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: GET_REPORT_QUERY,
                variables: {
                    id: parseInt(id, 10),
                },
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.toReport(apolloQueryResult.data.report));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async all(): Promise<NetworkResult<Report[]>> {
        try {
            const apolloQueryResult = await this.client.query<{reports: any[]}>({
                query: GET_REPORTS_QUERY,
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.reports.map((report) => this.toReport(report)));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async create(
        name: string,
        description: string | null,
        reportFilter: ReportFilter,
    ): Promise<NetworkResult<Report>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation CreateReport(
                        $name: String!
                        $description: String
                        $userIds: [Int]
                        $crewIds: [Int]
                        $snippetCollectionIds: [Int]
                        $from: String
                        $till: String
                    ) {
                        createReport(
                            name: $name
                            description: $description
                            user_ids: $userIds
                            crew_ids: $crewIds
                            snippet_collection_ids: $snippetCollectionIds
                            from_iso8601: $from
                            till_iso8601: $till
                        ) {
                            id
                            type
                            name
                            description
                            is_locked
                            user_ids
                            crew_ids
                            snippet_collection_ids
                            from_iso8601
                            till_iso8601
                            latest_snippet_at_iso8601
                            crews {
                                id
                                name
                            }
                            users {
                                id
                                display_name
                            }
                            snippetCollections {
                                id
                                name
                            }
                            template {
                                trends {
                                    title
                                    rows {
                                        type
                                        fields
                                        x
                                        y
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    name,
                    description,
                    snippetCollectionIds: reportFilter.snippetCollectionIds,
                    crewIds: reportFilter.crewIds,
                    userIds: reportFilter.userIds,
                    from: reportFilter.dateRange?.from?.toISOString(),
                    till: reportFilter.dateRange?.till?.toISOString(),
                },
                refetchQueries: [GET_REPORTS_QUERY],
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.toReport(apolloQueryResult.data.createReport));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }


    public async createWithType(
        name: string,
        description: string | null,
        type: ReportType,
    ): Promise<NetworkResult<Report>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation CreateReport(
                        $name: String!
                        $description: String
                        $type: ReportType
                    ) {
                        createReport(
                            name: $name
                            description: $description
                            type: $type
                        ) {
                            id
                            name
                            type
                            description
                        }
                    }
                `,
                variables: {
                    name,
                    description,
                    type,
                },
                refetchQueries: [{ query: GET_REPORTS_QUERY }],
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.toReport(apolloQueryResult.data.createReport));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async update(id: string, name: string, description: string, isLocked: boolean, template: ReportTemplate) {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation UpdateReport(
                        $id: ID!
                        $name: String!
                        $description: String
                        $isLocked: Boolean
                        $template: ReportTemplateInput
                    ) {
                        updateReport(
                            id: $id
                            name: $name
                            description: $description
                            is_locked: $isLocked
                            template: $template
                        ) {
                            id
                            type
                            name
                            description
                            is_locked
                            user_ids
                            crew_ids
                            snippet_collection_ids
                            from_iso8601
                            till_iso8601
                            latest_snippet_at_iso8601
                            crews {
                                id
                                name
                            }
                            users {
                                id
                                display_name
                            }
                            snippetCollections {
                                id
                                name
                            }
                            template {
                                trends {
                                    title
                                    rows {
                                        type
                                        fields
                                        x
                                        y
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    id,
                    name,
                    description,
                    isLocked,
                    template,
                },
                refetchQueries: [GET_REPORTS_QUERY, GET_REPORT_QUERY],
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.toReport(apolloQueryResult.data.createReport));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async delete(id: string): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation DeleteReport($id: ID!) {
                        deleteReport(id: $id)
                    }
                `,
                variables: {
                    id,
                },
                refetchQueries: [GET_REPORTS_QUERY],
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(null);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private toReport(response: any): Report {
        return {
            id: response.id,
            name: response.name,
            type: response.type,
            description: response.description,
            is_locked: response.is_locked,
            template: {
                trends: response.template?.trends?.map((trend: any) => ({
                    title: trend.title,
                    rows: trend.rows.map((row: any) => ({
                        type: row.type,
                        fields: row.fields,
                        x: row.x,
                        y: row.y,
                    })),
                })) || [],
            },
            user_ids: response.user_ids,
            crew_ids: response.crew_ids,
            snippet_collection_ids: response.snippet_collection_ids,
            from: response.from ? new Date(response.from) : null,
            till: response.till ? new Date(response.till) : null,
            latestSnippetAt: response.latest_snippet_at_iso8601 ? new Date(response.latest_snippet_at_iso8601) : null,
            crews: response.crews,
            users: response.users,
            snippetCollections: response.snippetCollections,
        };
    }
}
